import { useEffect, useState } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import {
  FileText,
  UploadCloud,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Receipt,
  RotateCcw,
  BarChart3,
  FilePlus2,
  LogOut,
  User as UserIcon,
  Shield,
  Trash2,
  Sparkles,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const NavItem = ({ to, icon: Icon, label, end, testid }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
        isActive
          ? "bg-white/10 text-white"
          : "text-zinc-400 hover:text-white hover:bg-white/5"
      }`
    }
  >
    <Icon size={16} strokeWidth={1.6} />
    {label}
  </NavLink>
);

export default function Layout({ children }) {
  const [profile, setProfile] = useState(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    const load = () => api.get("/settings/seller").then(({ data }) => setProfile(data)).catch(() => {});
    load();
    // refresh when another tab/page updates the profile (storage event or focus)
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const name = profile?.name || "Not configured";
  const gstin = profile?.gstin || "—";
  const needsSetup = !profile?.name || !profile?.gstin;

  return (
    <div className="min-h-screen flex bg-[#fafafa]">
      <aside className="w-64 shrink-0 bg-[#09090b] text-white flex flex-col p-5" data-testid="sidebar">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="brand-link">
          <div className="w-8 h-8 rounded-md bg-blue-600 flex items-center justify-center">
            <Receipt size={18} strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">PO → Invoice</div>
            <div className="text-[10px] text-zinc-400 uppercase tracking-wider">
              GST Tax Invoice Tool
            </div>
          </div>
        </Link>

        <nav className="flex flex-col gap-1">
          <NavItem to="/" end icon={UploadCloud} label="Dashboard" testid="nav-dashboard" />
          <NavItem to="/manual" icon={FilePlus2} label="Manual Invoice" testid="nav-manual" />
          <NavItem to="/analytics" icon={BarChart3} label="Analytics" testid="nav-analytics" />
          <NavItem to="/reverse" icon={RotateCcw} label="Reverse Invoice" testid="nav-reverse" />
          <NavItem to="/history" icon={HistoryIcon} label="Invoice History" testid="nav-history" />
          <NavItem to="/trash" icon={Trash2} label="Recycle Bin" testid="nav-trash" />
          <NavItem to="/settings" icon={SettingsIcon} label="Settings" testid="nav-settings" />
          {user?.is_superadmin && (
            <NavItem to="/admin" icon={Shield} label="Admin" testid="nav-admin" />
          )}
        </nav>

        <div className="mt-auto text-xs text-zinc-500 pt-6 border-t border-white/10">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={12} />
            <span className="uppercase tracking-wider">Seller</span>
          </div>
          <div className="text-zinc-300 leading-snug" data-testid="sidebar-seller-name">
            {name}
            <div className="mono text-[10px] text-zinc-500 mt-1" data-testid="sidebar-seller-gstin">
              GSTIN {gstin}
            </div>
            {needsSetup && (
              <Link
                to="/settings"
                className="inline-block mt-2 text-[10px] text-amber-400 hover:text-amber-300 underline uppercase tracking-wider"
                data-testid="sidebar-setup-link"
              >
                Set up seller →
              </Link>
            )}
          </div>
        </div>

        {/* Account + Logout */}
        <div className="mt-3 pt-3 border-t border-white/10" data-testid="sidebar-account">
          <div className="flex items-center gap-2 text-[11px] text-zinc-400 mb-2">
            <UserIcon size={12} />
            <span className="uppercase tracking-wider">Account</span>
          </div>
          {user?.name ? (
            <>
              <div className="text-xs text-zinc-200 leading-snug truncate font-medium" data-testid="sidebar-user-name">
                {user.name}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 truncate mono" data-testid="sidebar-user-email">
                {user.email}
              </div>
            </>
          ) : (
            <div className="text-xs text-zinc-300 leading-snug truncate" data-testid="sidebar-user-email">
              {user?.email || "—"}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="w-full mt-2 justify-start text-[11px] text-zinc-400 hover:text-white hover:bg-white/5 h-8 px-2"
            data-testid="sidebar-logout"
          >
            <LogOut size={13} className="mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        {user?.status === "trial" && (
          <div
            className="flex items-center gap-2 px-6 py-2 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs"
            data-testid="trial-banner"
          >
            <Sparkles size={14} className="text-amber-600 shrink-0" />
            <span>
              You are on a <b>free trial</b>. All features are unlocked —
              <b> contact us to activate your account</b> for continued access.
            </span>
          </div>
        )}
        <Outlet />
        {children}
      </main>
    </div>
  );
}
